import { homerunnerApiBaseUrl } from '../../hr'
import { okOrThrow } from '../../common/http'
import { useInfiniteQuery } from '@tanstack/react-query'
import { DateKey } from '../../types'
import { MultiCalendarFilter } from './config'

// Per-day availability entry — same shape as the single-property
// `property-calendar` endpoint, restricted to the requested range.
export type MultiCalDay = {
  date: DateKey
  available: number
  checkin: number
  checkout: number
  min_stay: number
  max_stay: number
  price: number
}

export type MultiCalProperty = {
  id: string
  slug: string | null
  title: string
  thumbnail: string | null
  /**
   * ISO 4217 code for this property's day-cell prices — Central sends it per
   * row since central#3125 (#1078). Optional: older Central builds omit it and
   * the grid falls back to the widget's `currency` setting.
   */
  currency_code?: string | null
  availability: Record<string, MultiCalDay>
}

/**
 * Currency to format a property row's day cells in (#1078, the #994 shape):
 * the feed row's `currency_code` when it looks like an ISO code, else the
 * widget setting. Lenient on purpose — the response is a plain cast, not a
 * Zod parse, so an absent or malformed value must never brick the grid.
 */
export function propertyCurrency(p: Pick<MultiCalProperty, 'currency_code'>, fallback: string): string {
  const code = typeof p.currency_code === 'string' ? p.currency_code.trim().toUpperCase() : ''
  return /^[A-Z]{3}$/.test(code) ? code : fallback
}

export type MultiCalPagination = {
  page: number
  pageSize: number
  totalPages: number
  totalItems: number
  hasNextPage: boolean
  hasPrevPage: boolean
}

/** Build a contiguous list of YYYY-MM-DD keys starting at `start` for `days`. */
export function buildDateRange(start: Date, days: number): DateKey[] {
  const keys: DateKey[] = []
  for (let i = 0; i < days; i++) {
    const d = new Date(start)
    d.setDate(d.getDate() + i)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    keys.push(`${y}-${m}-${day}` as DateKey)
  }
  return keys
}

export async function fetchFeedAvailability(
  feedId: number,
  start: DateKey,
  end: DateKey,
  page: number,
  limit: number,
  platformUuid?: string | null,
) {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/${feedId}/feed/availability`)
  url.searchParams.append('start', start)
  url.searchParams.append('end', end)
  url.searchParams.append('page', String(page))
  url.searchParams.append('limit', String(limit))
  if (platformUuid) url.searchParams.append('platform', platformUuid)

  const res = await fetch(url.toString())
  await okOrThrow(res, 'Failed to fetch feed availability')
  return await res.json() as {
    status: 'ok' | 'error'
    result: MultiCalProperty[]
    pagination: MultiCalPagination
    status_code: number
  }
}

export function useFeedAvailability(
  filter: MultiCalendarFilter,
  start: DateKey,
  end: DateKey,
  perPage: number,
) {
  return useInfiniteQuery({
    queryKey: ['feed-availability', filter.feed_id, filter.platform, start, end, perPage],
    queryFn: ({ pageParam = 1 }) =>
      fetchFeedAvailability(filter.feed_id, start, end, pageParam, perPage, filter.platform),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasNextPage ? lastPage.pagination.page + 1 : undefined,
    enabled: !!filter.feed_id,
  })
}
